import React, { useEffect, useState, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LandingHeader from "../../components/LandingHeader/Header";
import LandingFooter from "../../components/footer/LandingFooter";
import { contentContext } from "../../context/contentContext";

// Module-level variable - resets on browser refresh, persists on internal navigation
let homePageResourcesLoaded = false;

const HomePage = () => {
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const navigate = useNavigate();
    const [config, setConfig] = useState({});
    const [videoBase, setVideoBase] = useState("");
    let img = window.app?.img;
    let videoCDN = window.app?.video;
    const videoRef = useRef(null);
    const rafRef = useRef(null);
    const scrollRequestedRef = useRef(false);
    const lastScrollYRef = useRef(0);
    const { menus, setContentSearchFlag, inactiveFlag, setInactiveFlag, contentSearchFlag, clientData, setClientData, setRoute } = useContext(contentContext);
    const [isScrolling, setIsScrolling] = useState(false);
    const [glowWrapperActive, setGlowWrapperActive] = useState(false);
    const [stepperVisible, setStepperVisible] = useState(false);
    const [stepperProgress, setStepperProgress] = useState(0);
    const [activeStepperLabel, setActiveStepperLabel] = useState(0);
    const [navOpen, setNavOpen] = useState(false);

    const sectionsRef = useRef([]);
    const videoSectionsRef = useRef([]);
    const touchStartYRef = useRef(0);
    const scrollDebounceRef = useRef(null);
    const lastScrollPositionRef = useRef(0);
    const [isScrollingUp, setIsScrollingUp] = useState(false);

    // Loading state management - only show loader on browser refresh, not on internal navigation
    // homePageResourcesLoaded is a module-level variable that resets on browser refresh
    const isFirstLoad = !homePageResourcesLoaded;
    const [isPageLoading, setIsPageLoading] = useState(isFirstLoad);
    const [videosLoaded, setVideosLoaded] = useState({
        heroBgRing: !isFirstLoad,
        chaos: !isFirstLoad,
        greenlightSections: !isFirstLoad,
        endSection: !isFirstLoad
    });
    const [imagesLoaded, setImagesLoaded] = useState({
        marketplace: !isFirstLoad,
        productionHub: !isFirstLoad,
        liveDubbing: !isFirstLoad,
        monetizeAI: !isFirstLoad
    });

    // Check if all resources are loaded
    useEffect(() => {
        const allVideosLoaded = Object.values(videosLoaded).every(loaded => loaded);
        const allImagesLoaded = Object.values(imagesLoaded).every(loaded => loaded);

        if (allVideosLoaded && allImagesLoaded && videoBase) {
            setIsPageLoading(false);
            // Mark resources as loaded for subsequent navigations
            homePageResourcesLoaded = true;
        }
    }, [videosLoaded, imagesLoaded, videoBase]);

    // Timeout fallback - show page after 10 seconds even if not all resources loaded (only on first load)
    useEffect(() => {
        if (!isFirstLoad) return;

        const timeoutId = setTimeout(() => {
            if (isPageLoading) {
                setIsPageLoading(false);
                homePageResourcesLoaded = true;
            }
        }, 10000);

        return () => clearTimeout(timeoutId);
    }, []);

    // Preload images when videoBase and config are available (only on first load)
    useEffect(() => {
        if (!isFirstLoad) return;

        const imagesCDN = config?.common?.imagesCDN || window.app?.img;
        if (!videoBase || !imagesCDN) return;

        const imageUrls = [
            { key: 'marketplace', url: `${imagesCDN}orasi/client/resources/orasiv1/images/marketplace.png` },
            { key: 'productionHub', url: `${imagesCDN}orasi/client/resources/orasiv1/images/ProductionHub.png` },
            { key: 'liveDubbing', url: `${imagesCDN}orasi/client/resources/orasiv1/images/LiveDubbing.png` },
            { key: 'monetizeAI', url: `${imagesCDN}orasi/client/resources/orasiv1/images/Monetize-with-AI.png` }
        ];

        imageUrls.forEach(({ key, url }) => {
            const image = new Image();
            image.onload = () => {
                setImagesLoaded(prev => ({ ...prev, [key]: true }));
            };
            image.onerror = () => {
                // Mark as loaded even on error to prevent infinite loading
                setImagesLoaded(prev => ({ ...prev, [key]: true }));
            };
            image.src = url;
        });
    }, [videoBase, config]);

    // Video load handlers (only update state on first load)
    const handleVideoLoad = (videoKey) => {
        if (isFirstLoad) {
            setVideosLoaded(prev => ({ ...prev, [videoKey]: true }));
        }
    };

    const sections = [
        { id: 'heroSection', type: 'hero', glowSlide: null },
        { id: 'solutionsTitleSection', type: 'solutions-title', glowSlide: null },
        { id: 'solutionsTextSection', type: 'solutions-text', glowSlide: null },
        { id: 'chaosSection', type: 'chaos', glowSlide: null },
        { id: 'greenlightSection', type: 'greenlight', glowSlide: 0 },
        { id: 'broadcastSection', type: 'broadcast', glowSlide: 1 },
        { id: 'adaptationSection', type: 'adaptation', glowSlide: 2 },
        { id: 'archiveSection', type: 'archive', glowSlide: 3 },
        { id: 'endSection', type: 'end', glowSlide: null },
        { id: 'cardsSection', type: 'cards', glowSlide: null }
    ];

    const updateStepperState = (glowSlideIndex, scrollProgress = 0) => {
        const dotBase = glowSlideIndex * 33.333333333;
        const dotPercent = Math.min(100, dotBase + (scrollProgress * 33.333333333));
        setStepperProgress(dotPercent);
        setActiveStepperLabel(glowSlideIndex);
    };

    const isGlowSection = (sectionIndex) => {
        return sections[sectionIndex]?.glowSlide !== null;
    };

    const updateGlowWrapperState = (sectionIndex) => {
        const isInGlow = isGlowSection(sectionIndex);
        setGlowWrapperActive(isInGlow);
        setStepperVisible(isInGlow);

        if (isInGlow) {
            const slideIndex = sections[sectionIndex].glowSlide;
            updateStepperState(slideIndex);
        }
    };

    const transitionToSection = (fromIndex, toIndex) => {
        if (fromIndex === toIndex || isScrolling) return;
        if (toIndex < 0 || toIndex >= sections.length) return;

        setIsScrolling(true);

        // Use longer transition time on mobile for smoother experience
        const isMobile = window.innerWidth <= 768;
        const transitionDelay = isMobile ? 300 : 200;
        const scrollSettleTime = isMobile ? 800 : 600;

        setTimeout(() => {
            const toSection = sectionsRef.current[toIndex];
            if (toSection) {
                toSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }

            setCurrentSectionIndex(toIndex);
            updateGlowWrapperState(toIndex);

            setTimeout(() => {
                setIsScrolling(false);
            }, scrollSettleTime);
        }, transitionDelay);
    };

    const handleWheel = (e) => {
        if (isScrolling) {
            e.preventDefault();
            return;
        }

        if (e.deltaY > 30 && currentSectionIndex < sections.length - 1) {
            e.preventDefault();
            transitionToSection(currentSectionIndex, currentSectionIndex + 1);
        } else if (e.deltaY < -30 && currentSectionIndex > 0) {
            e.preventDefault();
            transitionToSection(currentSectionIndex, currentSectionIndex - 1);
        }
    };

    const handleTouchStart = (e) => {
        touchStartYRef.current = e.changedTouches[0].screenY;
    };

    const handleTouchEnd = (e) => {
        if (isScrolling) return;

        const touchEndY = e.changedTouches[0].screenY;
        const diff = touchStartYRef.current - touchEndY;
        const threshold = 50;

        if (diff > threshold && currentSectionIndex < sections.length - 1) {
            transitionToSection(currentSectionIndex, currentSectionIndex + 1);
        } else if (diff < -threshold && currentSectionIndex > 0) {
            transitionToSection(currentSectionIndex, currentSectionIndex - 1);
        }
    };

    const handleKeydown = (e) => {
        if (isScrolling) return;

        if (e.key === 'ArrowDown' || e.key === 'PageDown') {
            e.preventDefault();
            if (currentSectionIndex < sections.length - 1) {
                transitionToSection(currentSectionIndex, currentSectionIndex + 1);
            }
        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
            e.preventDefault();
            if (currentSectionIndex > 0) {
                transitionToSection(currentSectionIndex, currentSectionIndex - 1);
            }
        }
    };

    const handleScrollbarScroll = () => {
        // Detect scroll direction immediately (outside of debounce)
        const currentScrollPosition = window.scrollY || window.pageYOffset;
        const scrollingUp = currentScrollPosition < lastScrollPositionRef.current;
        setIsScrollingUp(scrollingUp);
        lastScrollPositionRef.current = currentScrollPosition;

        if (isScrolling) return;

        if (scrollDebounceRef.current) {
            clearTimeout(scrollDebounceRef.current);
        }

        // Use longer debounce on mobile for smoother experience
        const isMobile = window.innerWidth <= 768;
        const debounceTime = isMobile ? 150 : 100;

        scrollDebounceRef.current = setTimeout(() => {
            scrollDebounceRef.current = null;

            // Find which section is most visible in the viewport
            let maxVisibleArea = 0;
            let newSectionIndex = currentSectionIndex;
            let maxVisiblePercentage = 0;
            let foundVisible = false;

            for (let i = 0; i < sectionsRef.current.length; i++) {
                const section = sectionsRef.current[i];
                if (section) {
                    const rect = section.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;

                    // Calculate visible area of the section
                    const visibleTop = Math.max(0, rect.top);
                    const visibleBottom = Math.min(viewportHeight, rect.bottom);
                    const visibleHeight = Math.max(0, visibleBottom - visibleTop);

                    // Calculate percentage of section visible
                    const sectionHeight = rect.height;
                    const visiblePercentage = sectionHeight > 0 ? (visibleHeight / sectionHeight) : 0;

                    // Prioritize sections that are more than 30% visible
                    if (visibleHeight > maxVisibleArea && visiblePercentage > 0.3) {
                        maxVisibleArea = visibleHeight;
                        maxVisiblePercentage = visiblePercentage;
                        newSectionIndex = i;
                        foundVisible = true;
                    }
                }
            }

            // Special handling when scrolling up and no section is clearly visible
            // This handles the case when scrolling from footer up to cards section
            if (!foundVisible && scrollingUp) {
                // Check if we're near the bottom and should show cards section
                const lastSection = sectionsRef.current[sectionsRef.current.length - 1];
                if (lastSection) {
                    const rect = lastSection.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;

                    // If any part of the last section is visible, or we're just below it
                    if (rect.top < viewportHeight || (rect.bottom > 0 && rect.top < viewportHeight * 1.5)) {
                        newSectionIndex = sectionsRef.current.length - 1;
                        foundVisible = true;
                    }
                }
            }

            // Update section index if we found a valid section
            if (foundVisible && newSectionIndex !== currentSectionIndex) {
                setCurrentSectionIndex(newSectionIndex);
                updateGlowWrapperState(newSectionIndex);
            }
        }, debounceTime);
    };

    const handleStepperClick = (targetGlowIndex) => {
        const targetSection = sections.findIndex(s => s.glowSlide === targetGlowIndex);
        if (targetSection !== -1) {
            transitionToSection(currentSectionIndex, targetSection);
        }
    };

    useEffect(() => {
        updateGlowWrapperState(0);
        // Initialize scroll position
        lastScrollPositionRef.current = window.scrollY || window.pageYOffset;

        // Add CSS scroll-snap for mobile to prevent blank pages during fast scrolling
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
            const styleId = 'mobile-scroll-snap-style';
            if (!document.getElementById(styleId)) {
                const style = document.createElement('style');
                style.id = styleId;
                style.textContent = `
                    @media (max-width: 768px) {
                        html {
                            scroll-snap-type: y mandatory;
                            scroll-behavior: smooth;
                        }
                        .snap-section {
                            scroll-snap-align: start;
                            scroll-snap-stop: always;
                            min-height: 100vh;
                        }
                        .glow-section {
                            scroll-snap-align: start;
                            scroll-snap-stop: always;
                        }
                    }
                `;
                document.head.appendChild(style);
            }
        }

        return () => {
            const styleEl = document.getElementById('mobile-scroll-snap-style');
            if (styleEl) {
                styleEl.remove();
            }
        };
    }, []);

    useEffect(() => {
        window.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('scroll', handleScrollbarScroll, { passive: true });
        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchend', handleTouchEnd, { passive: true });
        window.addEventListener('keydown', handleKeydown);

        return () => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('scroll', handleScrollbarScroll);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('keydown', handleKeydown);
            if (scrollDebounceRef.current) {
                clearTimeout(scrollDebounceRef.current);
            }
        };
    }, [currentSectionIndex, isScrolling]);

    const getSectionClasses = (index) => {
        let classes = 'snap-section';

        if (index === currentSectionIndex) {
            classes += ' active';
        } else if (index < currentSectionIndex) {
            classes += ' blur-out';
        } else {
            classes += ' blur-in';
        }

        return classes;
    };

    const videoSections = [
        {
            startId: "solution-name-start",
            endId: "chaos-wrap",
            startTime: 0,
            endTime: 1.0
        },
        {
            startId: "chaos-wrap",
            endId: "greenlight-wrap",
            startTime: 1.0,
            endTime: 1.75
        },
        {
            startId: "greenlight-wrap",
            endId: "broadcast-wrap",
            startTime: 1.75,
            endTime: 3.5
        },
        {
            startId: "broadcast-wrap",
            endId: "solution-end-stop",
            startTime: 3.5,
            endTime: 13
        }
    ];

    useEffect(() => {
        if (window.site) {
            setConfig(window.site);
            const resourcesCDN = window.site?.common?.resourcesCDN;
            if (resourcesCDN) {
                setVideoBase(resourcesCDN);
            }
        }
    }, [window.site]);

    useEffect(() => {
        if (!videoBase) return;

        const resolveSections = () => {
            const resolved = videoSections.map(s => {
                const startEl = document.getElementById(s.startId);
                const endEl = document.getElementById(s.endId);
                const startTop = startEl ? startEl.getBoundingClientRect().top + window.scrollY : null;
                const endTop = endEl ? endEl.getBoundingClientRect().top + window.scrollY : null;
                const endBottom = endEl ? (endEl.getBoundingClientRect().bottom + window.scrollY) : endTop;
                return {
                    ...s,
                    startEl,
                    endEl,
                    startTop,
                    endTop,
                    endBottom
                };
            });
            videoSectionsRef.current = resolved;
        };

        resolveSections();
        const handleResize = () => {
            resolveSections();
            tick();
        };

        const handleScroll = () => {
            lastScrollYRef.current = window.scrollY;
            if (!scrollRequestedRef.current) {
                scrollRequestedRef.current = true;
                rafRef.current = requestAnimationFrame(tick);
            }
        };

        const videoEl = videoRef.current;
        let metadataReady = false;
        const onMetadata = () => {
            metadataReady = true;
            clampSectionTimesToDuration();
            tick();
        };

        const clampSectionTimesToDuration = () => {
            if (!videoEl || !videoEl.duration) return;
            const dur = videoEl.duration;
            videoSectionsRef.current = videoSectionsRef.current.map(s => {
                const st = Math.max(0, Math.min(s.startTime || 0, dur));
                const et = Math.max(0, Math.min(s.endTime || dur, dur));
                return { ...s, startTime: st, endTime: et };
            });
        };

        const tick = () => {
            scrollRequestedRef.current = false;
            const scrollY = lastScrollYRef.current;
            const v = videoRef.current;
            if (!v) return;
            const dur = v.duration || 0;

            let applied = false;

            for (let s of videoSectionsRef.current) {
                if (!s.startTop || !s.endTop) continue;

                const startOffset = s.startTop - window.innerHeight;
                const endOffset = s.endTop;
                const total = endOffset - startOffset;

                if (total <= 0) continue;

                const rawProgress = (scrollY - startOffset) / total;
                const progress = Math.max(0, Math.min(1, rawProgress));

                if (rawProgress >= 0 && rawProgress <= 1) {
                    const st = s.startTime;
                    const et = s.endTime;

                    let desired = st + (et - st) * progress;

                    // ⭐ FIX LAST SECTION: ALWAYS REACH 7s
                    const isLastSection = et === 7;
                    if (isLastSection && progress >= 1) {
                        v.currentTime = 7;
                        v.pause();
                        applied = true;
                        break;
                    }

                    if (!isNaN(desired) && Math.abs((v.currentTime || 0) - desired) > 0.04) {
                        v.currentTime = Math.max(0, Math.min(desired, dur));
                    }

                    v.pause();
                    applied = true;
                    break;
                }
            }

            if (!applied) {
                v.pause();
            }
        };


        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleResize, { passive: true });
        if (videoEl) {
            if (videoEl.readyState >= 1) {
                onMetadata();
            } else {
                videoEl.addEventListener("loadedmetadata", onMetadata);
            }
        }

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
            if (videoEl) videoEl.removeEventListener("loadedmetadata", onMetadata);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [videoBase]);

    if (config?.common?.imagesCDN) {
        img = config.common.imagesCDN;
    }

    // On first load, show loader while waiting for videoBase
    // On subsequent navigations, return null if videoBase not ready (shouldn't happen as it's cached)
    if (!videoBase) {
        if (isFirstLoad) {
            return (
                <div className="orasi-preloader">
                    <img src={"https://d2aif2zmoi437u.cloudfront.net/orasi/common/images/preloader.png"} alt="Loading..." />
                </div>
            );
        }
        return null;
    }
    const setSectionRef = (index) => (el) => {
        sectionsRef.current[index] = el;
    };

    return (
        <div className="font-[Saira] bg-[#0a0a0a] text-white">
            {/* Loading overlay - shown while resources are loading */}
            {isPageLoading && (
                <div className="orasi-preloader">
                    <img src={"https://d2aif2zmoi437u.cloudfront.net/orasi/common/images/preloader.png"} alt="Loading..." />
                </div>
            )}

            {/* Navigation */}
            <LandingHeader />

            {/* Scroll Container */}
            <div className="scroll-container mt-16">

                {/* Hero Section */}
                <section
                    // ref={el => sectionsRef.current[0] = el}
                    ref={setSectionRef(0)}
                    className={`${getSectionClasses(0)} hero-section`}
                >

                    <div className="hero-content">
                        <div className="container">
                            <div className="hero-text-wrapper mx-auto">
                                <p className="hero-subtitle">The world's only platform where you</p>
                                <h1 className="hero-title">Buy, sell, localize and greenlight in one ecosystem.</h1>
                                <p className="hero-description">
                                    From the idea's first spark to global audiences and back again through archives that
                                    fund new creations, we connect the entire media lifecycle in one sphere.
                                </p>
                                <button className="custom-btn-primary" onClick={() => {
                                    transitionToSection(currentSectionIndex, 1);
                                }} >
                                    <span>Explore the Ecosystem</span>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Metrics Overlay */}
                        <div className="metrics-overlay">
                            <div className="container">
                                <div className="row justify-content-center metrics-container g-4">
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">500K+</h3>
                                        <p className="">Hours of content licensed globally</p>
                                    </div>
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">300+</h3>
                                        <p className="">Live channels available to broadcasters</p>
                                    </div>
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">50+</h3>
                                        <p className="">Live-dubbed sports, news, and entertainment</p>
                                    </div>
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">$3M+</h3>
                                        <p className="">Paid out to partners through AI licensing</p>
                                    </div>
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">3K+</h3>
                                        <p className="">Producers simplified their production lifecycle</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Solutions Title Section */}
                <section
                    // ref={el => sectionsRef.current[1] = el}
                    ref={setSectionRef(1)}
                    className={`${getSectionClasses(1)} solutions-title-section ${isScrollingUp ? 'top' : ''}`}
                >
                    <div className="solutions-video-bg">
                        <video
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                            onCanPlayThrough={() => handleVideoLoad('heroBgRing')}
                            onLoadedData={() => handleVideoLoad('heroBgRing')}
                        >
                            <source
                                src={`${videoBase}/orasi/client/resources/orasiv1/videos/heroBgRing.mp4`}
                            />
                        </video>
                    </div>
                    <div className="solutions-content">
                        <div className="solutions-metrics-overlay">
                            <div className="container">
                                <div className="row justify-content-center metrics-container g-4">
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">500K+</h3>
                                        <p>Hours of content licensed globally</p>
                                    </div>
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">300+</h3>
                                        <p>Live channels available to broadcasters</p>
                                    </div>
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">50+</h3>
                                        <p>Live-dubbed sports, news, and entertainment</p>
                                    </div>
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">$3M+</h3>
                                        <p>Paid out to partners through AI licensing</p>
                                    </div>
                                    <div className="col-6 col-md metrics-item">
                                        <h3 className="fw-bold">3K+</h3>
                                        <p>Producers simplified their production lifecycle</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </section>

                {/* Solutions Text Section */}
                <section
                    // ref={el => sectionsRef.current[2] = el}
                    ref={setSectionRef(2)}
                    className={`${getSectionClasses(2)} solutions-text-section`}
                >
                    <div className="container mx-auto px-4 text-center">
                        <h2 className="section-main-title">Solutions</h2>
                    </div>
                </section>

                {/* The Chaos Section */}
                <section
                    // ref={el => sectionsRef.current[3] = el}
                    ref={setSectionRef(3)}
                    className={`${getSectionClasses(3)} chaos-section`}
                >
                    <div className="container mx-auto px-4 text-center">
                        <div className="section-label">The Chaos</div>
                        <h3 className="section-title">Every story begins with a spark.</h3>
                        <p className="section-desc">An idea, a script, a vision. But between concept and camera comes the chaos of budgets, logistics, crews, and constant fires. Orasi keeps the spark alive with one hub to plan, track, and manage it all.</p>
                        <video
                            className="spark-image"
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                            onCanPlayThrough={() => handleVideoLoad('chaos')}
                            onLoadedData={() => handleVideoLoad('chaos')}
                        >
                            {/* <source src="/assets/videos/chaos.mp4" type="video/mp4" /> */}
                            <source src={`${videoBase}/orasi/client/resources/orasiv1/videos/chaos.mp4`} type="video/mp4" />

                        </video>
                    </div>
                </section>

                {/* Glow Wrapper */}
                <div className={`glow-wrapper ${glowWrapperActive ? 'active' : ''} ${isScrollingUp ? 'top' : ''}`}>
                    <div className="glow-ring">
                        <video
                            className="ring-image"
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                            onCanPlayThrough={() => handleVideoLoad('greenlightSections')}
                            onLoadedData={() => handleVideoLoad('greenlightSections')}
                        >
                            {/* <source src="/assets/videos/greenlight-sections.mp4" type="video/mp4" /> */}
                            <source src={`${videoBase}/orasi/client/resources/orasiv1/videos/greenlight-sections.mp4`} type="video/mp4" />
                        </video>

                    </div>

                    {/* The Greenlight */}
                    <section
                        // ref={el => sectionsRef.current[4] = el}
                        ref={setSectionRef(4)}
                        className={`${getSectionClasses(4)} glow-section greenlight-section`}
                    >
                        <div className="container mx-auto px-4 text-center">
                            <div className="section-label">The Greenlight</div>
                            <h3 className="section-title">From pitch to greenlight, friction disappears.</h3>
                            <p className="section-desc">Funding drags. Spreadsheets pile up. Stories stall. Orasi clears
                                the bottleneck. With trusted workflows and a marketplace built for speed, your project moves
                                faster from pitch to greenlight.</p>
                            <button className="custom-btn-primary" onClick={() => { navigate("/productioncircle"); window.scrollTo(0, 0); }}>
                                <span>Production Circle</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                            </button>
                        </div>
                    </section>

                    {/* The Broadcast */}
                    <section
                        // ref={el => sectionsRef.current[5] = el}
                        ref={setSectionRef(5)}
                        className={`${getSectionClasses(5)} glow-section broadcast-section`}
                    >
                        <div className="container mx-auto px-4 text-center">
                            <div className="section-label">The Broadcast</div>
                            <h3 className="section-title">Every story deserves an audience</h3>
                            <p className="section-desc">Licensing is slow, fragmented, buried in catalogues. Broadcasters spend more time chasing than streaming. Orasi opens the gates: 500,000+ hours, 300+ channels, every genre, every language, everywhere.</p>
                            <button className="custom-btn-primary" onClick={() => { navigate("/marketplace"); window.scrollTo(0, 0); }}>
                                <span>Marketplace</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                            </button>
                        </div>
                    </section>

                    {/* The Adaptation/Localization */}
                    <section
                        // ref={el => sectionsRef.current[6] = el}
                        ref={setSectionRef(6)}
                        className={`${getSectionClasses(6)} glow-section adaptation-section`}
                    >
                        <div className="container mx-auto px-4 text-center">
                            <div className="section-label">The Localization</div>
                            <h3 className="section-title">One story, infinite audiences</h3>
                            <p className="section-desc">A great show should never stop at a border. Yet language holds too many stories back. Orasi makes every program local - with AI-powered dubbing, real-time subtitles, expressive voices, and even sign language, so content speaks to audiences everywhere in 50+ languages.</p>
                            <button className="custom-btn-primary" onClick={() => { navigate("/aidubbing"); window.scrollTo(0, 0); }}>
                                <span>AI Dubbing</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                            </button>
                        </div>
                    </section>

                    {/* The Archive */}
                    <section
                        // ref={el => sectionsRef.current[7] = el}
                        ref={setSectionRef(7)}
                        className={`${getSectionClasses(7)} glow-section archive-section`}
                    >
                        <div className="container mx-auto px-4 text-center">
                            <div className="section-label">The Archive</div>
                            <h3 className="section-title">No story truly ever ends.</h3>
                            <p className="section-desc">When a title leaves the spotlight, its value doesn't disappear. Unsold hours and forgotten archives still hold worth. Orasi unlocks it. Licensing archives to AI companies has already paid out millions, turning old stories into new revenue.</p>
                            <button className="custom-btn-primary" onClick={() => { navigate("/ailicensing"); window.scrollTo(0, 0); }}>
                                <span>Licensing</span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                            </button>
                        </div>
                    </section>

                    {/* Stepper Navigation */}
                    <div className={`stepper-nav ${stepperVisible ? 'visible' : ''}`}>
                        <div className="stepper-labels">
                            {['The Greenlight', 'The Broadcast', 'The Adaptation', 'The Archive'].map((label, i) => (
                                <span
                                    key={i}
                                    className={`stepper-label ${i === activeStepperLabel ? 'active' : i < activeStepperLabel ? 'passed' : ''}`}
                                    onClick={() => handleStepperClick(i)}
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                        <div className="stepper-bar">
                            <div className="stepper-progress" style={{ width: `${stepperProgress}%` }}></div>
                            <div className="stepper-dot" style={{ left: `${stepperProgress}%` }}></div>
                        </div>
                    </div>
                </div>

                {/* The End Section */}
                <section
                    ref={el => sectionsRef.current[8] = el}
                    className={`${getSectionClasses(8)} end-section ${isScrollingUp ? 'top' : ''}`}
                >
                    <div className="container mx-auto px-4 text-center">
                        <div className="section-label">The End?</div>
                        <h3 className="section-title">And the circle begins again...</h3>
                        <p className="section-desc">From spark to greenlight, from broadcast to adaptation, from archive to new creation, Orasi keeps the cycle unbroken. We are the only platform where producers, broadcasters, distributors, and innovators meet in one sphere.</p>
                        <video
                            className="end-ring-image"
                            autoPlay
                            loop
                            muted
                            playsInline
                            preload="auto"
                            onCanPlayThrough={() => handleVideoLoad('endSection')}
                            onLoadedData={() => handleVideoLoad('endSection')}
                        >
                            {/* <source src="/assets/videos/greenlight-sections.mp4" type="video/mp4" />
                             */}
                            <source src={`${videoBase}/orasi/client/resources/orasiv1/videos/greenlight-sections.mp4`} type="video/mp4" />
                        </video>
                    </div>
                </section>

                {/* Cards Section */}
                <section
                    ref={el => sectionsRef.current[9] = el}
                    className={`${getSectionClasses(9)} cards-section card-section-wrapper`}
                >
                    <div className="container-fluid px-4">
                        <div className="row g-4">
                            <div className="col-lg-3 col-md-6">
                                <div className="service-card" data-aos="fade-up" data-aos-duration="1000">
                                    <div className="card-image" >
                                        <img src={`${img}orasi/client/resources/orasiv1/images/marketplace.png`} alt="Marketplace" className="img-fluid" />
                                    </div>
                                    <div className="card-content">
                                        <div >
                                            <h5 >Marketplace</h5>
                                            <p className="mt-2">Global catalog of films, series, and channels—buy, sell, or license content in 50+ languages.</p>
                                        </div>
                                        <button className="btn card-btn" onClick={() => { navigate("/marketplace"); window.scrollTo(0, 0); }}>
                                            <span>Explore</span>
                                            <span className="material-icons">chevron_right</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 col-md-6">
                                <div className="service-card" data-aos="fade-up" data-aos-duration="1400">
                                    <div className="card-image">
                                        <img src={`${img}orasi/client/resources/orasiv1/images/ProductionHub.png`} alt="Production Circle" className="img-fluid" />
                                    </div>
                                    <div className="card-content">
                                        <div>
                                            <h5 >Production Circle</h5>
                                            <p className="mt-2" >Manage your entire production process in one collaborative platform.</p>
                                        </div>
                                        <button className="btn card-btn" onClick={() => { navigate("/productioncircle"); window.scrollTo(0, 0); }}>
                                            <span>Explore</span>
                                            <span className="material-icons">chevron_right</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 col-md-6">
                                <div className="service-card" data-aos="fade-up" data-aos-duration="1800">
                                    <div className="card-image">
                                        <img src={`${img}orasi/client/resources/orasiv1/images/LiveDubbing.png`} alt="Live Dubbing" className="img-fluid" />
                                    </div>
                                    <div className="card-content">
                                        <div>
                                            <h5 >Live Dubbing</h5>
                                            <p className="mt-2" >Stream in any language with real-time AI dubbing and subtitling.</p>
                                        </div>
                                        <button className="btn card-btn" onClick={() => { navigate("/aidubbing"); window.scrollTo(0, 0); }}>
                                            <span>Explore</span>
                                            <span className="material-icons">chevron_right</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 col-md-6">
                                <div className="service-card" data-aos="fade-up" data-aos-duration="1800">
                                    <div className="card-image" >
                                        <img src={`${img}orasi/client/resources/orasiv1/images/Monetize-with-AI.png`} alt="Monetize with AI" className="img-fluid" />
                                    </div>
                                    <div className="card-content">
                                        <div>
                                            <h5  >Monetize with AI</h5>
                                            <p className="mt-2" >Monetize archived footage through secure AI training partnerships.</p>
                                        </div>
                                        <button className="btn card-btn" onClick={() => { navigate("/ailicensing"); window.scrollTo(0, 0); }} >
                                            <span>Explore</span>
                                            <span className="material-icons">chevron_right</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

            </div>

            {/* Footer */}
            <LandingFooter menus={menus} />
        </div>
    );
};

export default HomePage;
